export type Point = { x: number; y: number };

// Monotone cubic Hermite tangent computation shared by path builders
function computeTangents(points: Point[]): number[] {
  const n = points.length;
  const deltas: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    deltas.push(
      (points[i + 1]!.y - points[i]!.y) / (points[i + 1]!.x - points[i]!.x)
    );
  }

  const tangents: number[] = new Array(n);
  tangents[0] = deltas[0]!;
  tangents[n - 1] = deltas[n - 2]!;
  for (let i = 1; i < n - 1; i++) {
    if (deltas[i - 1]! * deltas[i]! <= 0) {
      tangents[i] = 0;
    } else {
      tangents[i] = (deltas[i - 1]! + deltas[i]!) / 2;
    }
  }

  // Fritsch-Carlson monotonicity fix
  for (let i = 0; i < n - 1; i++) {
    if (Math.abs(deltas[i]!) < 1e-12) {
      tangents[i] = 0;
      tangents[i + 1] = 0;
    } else {
      const alpha = tangents[i]! / deltas[i]!;
      const beta = tangents[i + 1]! / deltas[i]!;
      const s = alpha * alpha + beta * beta;
      if (s > 9) {
        const t = 3 / Math.sqrt(s);
        tangents[i] = t * alpha * deltas[i]!;
        tangents[i + 1] = t * beta * deltas[i]!;
      }
    }
  }

  return tangents;
}

export function smoothPath(points: Point[]): string {
  if (points.length < 2) return "";
  if (points.length === 2) {
    return `M${points[0]!.x},${points[0]!.y}L${points[1]!.x},${points[1]!.y}`;
  }

  const tangents = computeTangents(points);
  let d = `M${points[0]!.x},${points[0]!.y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const dx = (points[i + 1]!.x - points[i]!.x) / 2;
    const cp1x = points[i]!.x + dx;
    const cp1y = points[i]!.y + tangents[i]! * dx;
    const cp2x = points[i + 1]!.x - dx;
    const cp2y = points[i + 1]!.y - tangents[i + 1]! * dx;
    d += `C${cp1x},${cp1y},${cp2x},${cp2y},${points[i + 1]!.x},${points[i + 1]!.y}`;
  }
  return d;
}
